import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X, MessageSquareWarning } from 'lucide-react';
import { Icon } from '../design/Icon';
import { openFeedback } from '../feedback/FeedbackButton';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Width class — defaults to max-w-lg. */
  widthClass?: string;
  /** False hides the close button (a blocking step such as the mandatory profile). Escape and
   *  click-outside still call onClose, so pass a no-op there too. */
  dismissible?: boolean;
}

/**
 * Modal — surface panel on a blurred, translucent page-ground backdrop.
 *
 * Design (design system v1, Sep 2026):
 *   - Backdrop: bg-app/70 + backdrop blur (follows the theme)
 *   - Panel: rounded-xl surface with a line border and shadow-lg
 *   - Header: panel-internal divider via border-line
 *
 * Portalled to <body>: rendered in place, the fixed overlay inherits layout from
 * its parent. Inside a page's `space-y-*` main it picked up a margin-top and slid
 * down, leaving an unblurred strip over the top bar; inside the top bar the bar's
 * backdrop-blur became its containing block. On <body> neither can happen.
 *
 * a11y: focus traps to the modal contents on open, restores focus on close,
 * Escape closes, click-outside closes. We avoid a heavy dialog library
 * because the design language wants very specific styling that's easier
 * to own directly.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  widthClass = 'max-w-lg',
  dismissible = true,
}: ModalProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const previousActiveRef = useRef<Element | null>(null);

  // Keep the latest onClose in a ref so the focus-management effect below can
  // depend ONLY on `open`. Callers almost always pass an inline arrow (a new
  // identity every render); if the effect depended on onClose it would re-run
  // on every parent re-render and call cardRef.focus(), stealing focus out of
  // whatever input the user is typing in (the "type one char then it jumps
  // out" bug). Depending only on `open` runs it once per open/close.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Remember what had focus BEFORE the modal opened. Captured during render,
  // because an autoFocus field inside the modal takes focus at commit, before
  // any effect runs; reading document.activeElement in the effect would record
  // that field instead of the opener.
  const wasOpenRef = useRef(false);
  if (open && !wasOpenRef.current) previousActiveRef.current = document.activeElement;
  wasOpenRef.current = open;

  useEffect(() => {
    if (!open) return;
    // Leave focus on an autoFocus field inside the modal; otherwise focus the panel.
    if (!cardRef.current?.contains(document.activeElement)) cardRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      if (previousActiveRef.current instanceof HTMLElement) {
        previousActiveRef.current.focus();
      }
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-app/70 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      aria-hidden={false}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
        className={`bg-surface text-fg border border-line rounded-xl shadow-lg w-full ${widthClass} max-h-[90vh] overflow-auto`}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-4 sm:px-6 sm:py-5 border-b border-line">
          <h2
            id="modal-title"
            className="min-w-0 break-words font-sans tracking-tight text-lg font-semibold text-fg"
          >
            {title}
          </h2>
          <div className="shrink-0 inline-flex items-center -mr-2">
            {/* The top bar's feedback button sits under this overlay, so offer it here too. */}
            <button
              type="button"
              onClick={openFeedback}
              aria-label="Report a problem"
              title="Report a problem"
              className="inline-flex items-center justify-center h-8 w-8 rounded-md text-fg-faint hover:text-fg hover:bg-surface-2 transition-colors"
            >
              <Icon as={MessageSquareWarning} size={16} />
            </button>
            {dismissible && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close dialog"
                className="inline-flex items-center justify-center h-8 w-8 rounded-md text-fg-faint hover:text-fg hover:bg-surface-2 transition-colors"
              >
                <Icon as={X} />
              </button>
            )}
          </div>
        </div>
        <div className="p-4 sm:p-6 text-fg">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
